import { useQuery, useMutation, FetchResult } from "@apollo/client";
import { FormikHelpers, Formik, Form } from "formik";
import { useSnackbar } from "notistack";
import * as yup from "yup";

import { MenuItem } from "@material-ui/core";

import { useTenant } from "src/components/LoggedInAdminProvider";
import FormikErrorBanner from "src/components/form/FormikErrorBanner";
import { FormikSelect } from "src/components/form/Select";
import SubmitButton from "src/components/form/SubmitButton";
import { listTwilioSubaccounts } from "src/graphql/generated/listTwilioSubaccounts";
import {
  twilioSubaccountSelect,
  twilioSubaccountSelectVariables
} from "src/graphql/generated/twilioSubaccountSelect";
import {
  LIST_TWILIO_SUBACCOUNT,
  SELECT_TWILIO_SUBACCOUNT
} from "src/graphql/twilioSubaccount";
import palette from "src/styles/palette";

type Values = {
  twilioSubaccountId: string;
};

export function TwilioSubaccountSelectForTenant(): JSX.Element {
  const [twilioSubaccountSelect] = useMutation<
    twilioSubaccountSelect,
    twilioSubaccountSelectVariables
  >(SELECT_TWILIO_SUBACCOUNT);

  const onSubmit = ({
    twilioSubaccountId
  }: {
    twilioSubaccountId: string;
  }): Promise<FetchResult<any>> =>
    twilioSubaccountSelect({
      variables: {
        twilioSubaccountId:
          twilioSubaccountId === "default" ? null : twilioSubaccountId
      }
    });

  return <TwilioSubaccountSelectForm onSubmit={onSubmit} />;
}

export function TwilioSubaccountSelectForm({
  onSubmit,
  twilioSubaccountId
}: {
  onSubmit: ({
    twilioSubaccountId
  }: {
    twilioSubaccountId: string;
  }) => Promise<{ errors?: any }>;
  twilioSubaccountId?: string;
}): JSX.Element {
  const tenant = useTenant()!;
  const snackbar = useSnackbar();

  const { data } = useQuery<listTwilioSubaccounts>(LIST_TWILIO_SUBACCOUNT);

  const schema = yup.object().shape({
    twilioSubaccountId: yup.string().required("Required")
  });

  const handleSubmit = async (
    values: Values,
    helpers: FormikHelpers<Values>
  ) => {
    const { errors } = await onSubmit(values);
    if (!errors) {
      snackbar.enqueueSnackbar("Twilio configuration updated.", {
        variant: "info"
      });
      helpers.resetForm({
        values: {
          twilioSubaccountId: values.twilioSubaccountId
        }
      });
    } else {
      snackbar.enqueueSnackbar(errors[0].message, { variant: "error" });
      helpers.resetForm();
    }
  };

  return (
    <Formik
      initialValues={{
        twilioSubaccountId: twilioSubaccountId || tenant.twilioSubaccount.id
      }}
      onSubmit={handleSubmit}
      validationSchema={schema}
    >
      {({ isSubmitting, dirty }) => (
        <Form>
          <FormikErrorBanner />
          <FormikSelect
            fullWidth
            name="twilioSubaccountId"
            label="Selected Twilio configuration"
          >
            {data?.listTwilioSubaccounts &&
              data?.listTwilioSubaccounts.map((ts, index) => (
                <MenuItem
                  key={`TwilioSubaccount_${index}`}
                  value={ts.id}
                  divider
                >
                  <div style={ts.deletedAt ? { color: palette.red } : {}}>
                    <strong>
                      {ts.deletedAt ? "[INACTIVE] " : null}
                      {ts.name}
                    </strong>
                    <br />
                    AccountSid: {ts.accountSid}
                    <br />
                    API Key: {ts.apiKey}
                  </div>
                </MenuItem>
              ))}
          </FormikSelect>
          <SubmitButton
            type="submit"
            disabled={!dirty}
            isSubmitting={isSubmitting}
            submitText="Update"
            loadingText="updating..."
          />
        </Form>
      )}
    </Formik>
  );
}
